/* eslint-disable */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  RiUserLine,
  RiMailLine,
  RiLockLine,
  RiEyeLine,
  RiEyeOffLine,
  RiArrowLeftLine,
  RiShieldCheckLine
} from 'react-icons/ri';
import toast from 'react-hot-toast';

const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'editor'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const navigate = useNavigate();
  const { register } = useAuth();

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    
    try {
      const { confirmPassword, ...userData } = formData;
      
      const result = await register(userData);
      
      if (result.success) {
        toast.success('Registration successful! Please wait for admin approval.');
        setTimeout(() => {
          navigate('/admin/login');
        }, 2000);
      } else {
        toast.error(result.error || 'Registration failed');
      }
    } catch (error) {
      toast.error('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNavigateToLogin = () => {
    navigate('/admin/login');
  };

  return (
    <div className="register-page">
      <div className="register-container">
        <div className="register-header">
          <button
            className="back-button"
            onClick={handleNavigateToLogin}
            disabled={isLoading}
          >
            <RiArrowLeftLine />
          </button>
          
          <div className="logo">
            <img src="/assets/img/logo.png" alt="FARMO SOUTH SUDAN" />
            <div className="logo-text">
              <h1>FARMO SOUTH SUDAN</h1>
              <p>Admin Registration</p>
            </div>
          </div>
          
          <h2>Create Account</h2>
          <p className="subtitle">Register for access to the admin dashboard</p>
        </div>

        <form onSubmit={handleSubmit} className="register-form">
          <div className="form-group">
            <label htmlFor="name">
              <RiUserLine /> Full Name
            </label>
            <input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your full name"
              required
              disabled={isLoading}
              className={`form-input ${errors.name ? 'error' : ''}`}
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="email">
              <RiMailLine /> Email Address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              required
              disabled={isLoading}
              className={`form-input ${errors.email ? 'error' : ''}`}
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="password">
              <RiLockLine /> Password
            </label>
            <div className="password-input">
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={handleChange}
                placeholder="Create a password"
                required
                disabled={isLoading}
                className={`form-input ${errors.password ? 'error' : ''}`}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoading}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <RiEyeOffLine /> : <RiEyeLine />}
              </button>
            </div>
            {errors.password && <span className="error-message">{errors.password}</span>}
            <div className="password-hints">
              <small>Password must be at least 6 characters long</small>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">
              <RiLockLine /> Confirm Password
            </label>
            <div className="password-input">
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your password"
                required
                disabled={isLoading}
                className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                disabled={isLoading}
                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
              >
                {showConfirmPassword ? <RiEyeOffLine /> : <RiEyeLine />}
              </button>
            </div>
            {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="role">
              <RiShieldCheckLine /> User Role
            </label>
            <select
              id="role"
              name="role"
              value={formData.role}
              onChange={handleChange}
              disabled={isLoading}
              className="form-input"
            >
              <option value="editor">Editor (Can manage content)</option>
              <option value="admin">Administrator (Full access)</option>
            </select>
            <small className="role-hint">
              Note: Admin roles require approval from existing administrators
            </small>
          </div>

          <div className="terms-agreement">
            <label className="checkbox">
              <input type="checkbox" required />
              <span>
                I agree to the <a href="/terms">Terms of Service</a> and <a href="/privacy">Privacy Policy</a>
              </span>
            </label>
          </div>

          <button 
            type="submit" 
            className="btn btn-primary btn-block"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner"></span> Creating Account...
              </>
            ) : (
              'Create Account'
            )}
          </button>

          <div className="login-redirect">
            <p>
              Already have an account?{' '}
              <button
                type="button"
                className="link-button"
                onClick={handleNavigateToLogin}
                disabled={isLoading}
              >
                Sign in here
              </button>
            </p>
          </div>
        </form>

        <div className="register-footer">
          <div className="security-info">
            <RiShieldCheckLine className="security-icon" />
            <p>Your information is secured with enterprise-grade encryption</p>
          </div>
          
          <p className="copyright">
            &copy; {new Date().getFullYear()} Farmo South Sudan.Org
          </p>
        </div>
      </div>

      <div className="register-sidebar">
        <div className="sidebar-content">
          <div className="sidebar-header">
            <h3>Account Benefits</h3>
            <p>Here's what you get with a Farmo South Sudan account</p>
          </div>
          
          <div className="benefits-list">
            <div className="benefit-item">
              <div className="benefit-icon">🎯</div>
              <div>
                <h4>Full Access</h4>
                <p>Manage products, content, sponsors, and appointments</p>
              </div>
            </div>
            
            <div className="benefit-item">
              <div className="benefit-icon">📊</div>
              <div>
                <h4>Analytics Dashboard</h4>
                <p>Track performance with real-time analytics</p>
              </div>
            </div>
            
            <div className="benefit-item">
              <div className="benefit-icon">🔒</div>
              <div>
                <h4>Secure Access</h4>
                <p>Enterprise-grade security with role-based permissions</p>
              </div>
            </div>
            
            <div className="benefit-item">
              <div className="benefit-icon">📱</div>
              <div>
                <h4>Responsive Design</h4>
                <p>Access from any device, anywhere</p>
              </div>
            </div>
          </div>
          
          <div className="registration-notes">
            <h4>Important Notes</h4>
            <ul>
              <li>All new accounts require admin approval</li>
              <li>You'll receive an email once your account is activated</li>
              <li>Keep your login credentials secure</li>
              <li>Contact support if you need assistance</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;